"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getFirebaseFirestore } from "@/lib/firebase";
import styles from "./ContactForm.module.css";
// import posthog from "posthog-js";

const CONTACT_IMG = "/Images/contact-us-master-utils.webp";
const FIRESTORE_COLLECTION = "master-utils";

const EMAIL_REGEX =
  /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;

type ContactFormValue = {
  name?: string;
  email?: string;
  message?: string;
};

export default function ContactForm() {
  const t = useTranslations("contact");
  const [inputValue, setInputValue] = useState<ContactFormValue>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setInputValue((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
    setSubmitError(null);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    const email = inputValue.email?.trim();
    if (email && !EMAIL_REGEX.test(email)) {
      newErrors["email"] = t("form.invalidEmail");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setSubmitError(null);
    try {
      const db = getFirebaseFirestore();
      const formDetails = JSON.stringify({
        name: inputValue.name?.trim() ?? "",
        email: inputValue.email?.trim() ?? "",
        message: inputValue.message?.trim() ?? "",
      });
      await addDoc(collection(db, FIRESTORE_COLLECTION), {
        formDetails,
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
      setInputValue({ name: "", email: "", message: "" });
      // posthog.capture('contact_form_submitted', {
      //   has_name: Boolean(inputValue.name?.trim()),
      //   has_email: Boolean(inputValue.email?.trim()),
      //   has_message: Boolean(inputValue.message?.trim()),
      // });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t("form.errorMessage");
      setSubmitError(errorMessage);
      // posthog.capture('contact_form_failed', {
      //   error_message: errorMessage,
      // });
      // posthog.captureException(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  };

  const optionalLabel = ` (${t("form.optional")})`;

  return (
    <div>
      <div className="container-md">
        <div className={styles.contactFormAllSectionContentAlignment}>
          <div className={styles.contactFromAlignment}>
            <div className={styles.formGridAlignment}>
              <div className={styles.formDetailsAlignment}>
                <div className={styles.formDetailsHeadingAlignment}>
                  <h4>{t("form.getInTouch")}</h4>
                  <span>{t("form.subtitle")}</span>
                </div>

                <form onSubmit={handleSubmit} noValidate>
                  <div className={styles.inputAlignment}>
                    <label htmlFor="contact-name">
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        autoComplete="name"
                        placeholder=" "
                        value={inputValue.name ?? ""}
                        onChange={handleChange}
                        aria-invalid={Boolean(errors.name)}
                        aria-describedby={errors.name ? "contact-name-error" : undefined}
                      />
                      <span>{t("form.nameLabel")}{optionalLabel}</span>
                    </label>
                    {errors.name && (
                      <p id="contact-name-error" className="text-sm text-red-700" role="alert">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div className={styles.inputAlignment}>
                    <label htmlFor="contact-email">
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        autoComplete="email"
                        placeholder=" "
                        value={inputValue.email ?? ""}
                        onChange={handleChange}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={errors.email ? "contact-email-error" : undefined}
                      />
                      <span>{t("form.emailLabel")}{optionalLabel}</span>
                    </label>
                    {errors.email && (
                      <p id="contact-email-error" className="text-sm text-red-700" role="alert">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div className={styles.inputAlignment}>
                    <label htmlFor="contact-message">
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={4}
                        placeholder=" "
                        value={inputValue.message ?? ""}
                        onChange={handleChange}
                        className={errors.message ? "error-input" : ""}
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={errors.message ? "contact-message-error" : undefined}
                      />
                      <span>{t("form.messageLabel")}{optionalLabel}</span>
                    </label>
                    {errors.message && (
                      <p id="contact-message-error" className="text-sm text-red-700" role="alert">
                        {errors.message}
                      </p>
                    )}
                  </div>

                  {submitted && (
                    <p className="text-sm text-green-600 mb-2" role="status">
                      {t("form.successMessage")}
                    </p>
                  )}
                  {submitError && (
                    <p className="text-sm text-red-700 mb-2" role="alert">
                      {submitError}
                    </p>
                  )}

                  <div className={styles.buttonAlignment}>
                    <button type="submit" disabled={loading}>
                      <span>{loading ? t("form.sending") : t("form.send")}</span>
                    </button>
                  </div>
                </form>
              </div>

              <div className={styles.secondDetailsAlignment}>
                <img src={CONTACT_IMG} alt="" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
